/**
 * viewport-sizer — cross-browser geometry foundation (owner-approved 2026-05-28,
 * design-tokens-unified.md §14.9; rollout T-7.2.5-fix2).
 *
 * WHY THIS EXISTS (root cause of the SS-1570/1571 cross-browser incident):
 *   The board geometry was derived in CSS from `100vw`/`100dvh`/`vmin`. Browsers
 *   resolve `100vw` INCONSISTENTLY w.r.t. the classic scrollbar (Chrome includes
 *   it, Firefox subtracts it; Chrome 145+ changed again). Any formula built on
 *   `100vw` therefore renders differently in Chrome vs Firefox — confirmed by
 *   real-Chrome reproduction (daily board broke in Chrome, correct in Firefox).
 *
 * APPROACH (industry best practice — measure the container, not the window):
 *   Read `document.documentElement.clientWidth/clientHeight` — the layout
 *   viewport EXCLUDING the scrollbar, reported identically across engines as
 *   integer pixels. Compute the single source-of-truth `--cell-size` (and the
 *   `--m` delta) in JS, where the math is byte-identical everywhere. Everything
 *   else in CSS stays proportional to `--cell-size` exactly as before — and any
 *   `round()`/`calc()` now operates on a concrete px value, so it is portable.
 *
 *   ResizeObserver keeps it live on resize/rotate (96%+ browser support).
 *
 * SCOPE: owns `--cell-size` + `--m` + `--rail-w` on :root AND the deterministic
 * layout-switch (`:root[data-layout="rail"|"stacked"]`). The CSS layout-switch
 * keys off this data attribute — NOT off `@media (aspect-ratio)` — so the
 * stacked-vs-side-rail decision no longer depends on a viewport unit (it was the
 * last 100vw-scrollbar vector left in a LAYOUT decision; §14.9 point 4). The
 * aspect threshold is the same `4/5` the CSS used (board height-bound onset), now
 * evaluated once over the measured container. Device-type media (`hover/pointer`)
 * + the `?`-popover logic stay in CSS. Geometry constants mirror §3.2.
 *
 * «ПОСТАМЕНТ» — CENTRED BOARD ANCHOR (§14.10, owner-directive 2026-05-28):
 *   After the cross-browser foundation landed, the side-rail layout used a grid
 *   `minmax(0, rail_w) max-content rail_w` whose left spacer COLLAPSED on a
 *   typical window → the board drifted left of centre (the 0c1bbca bug; owner:
 *   "в Firefox доска ВСЕГДА была в центре, сейчас НЕ в центре"). The fix restores
 *   the board as a true-centred visual anchor:
 *     1. The board sits in the TRUE centre of the viewport (justify-content on a
 *        full-width block screen-root — cross-browser safe, the panel is
 *        position:absolute and does not affect centring).
 *     2. The cell's landscape width-bound term now reserves a SYMMETRIC margin
 *        (2× rail_w_min, one each side) so the centred board never hits the panel.
 *     3. `--rail-w` is a FLEX width (computeRailW): full (5·cell) when it fits,
 *        shrinking to rail_w_min as the window narrows, keeping the board centred
 *        and the board↔panel gap ≥ delta. The board itself only shrinks at the
 *        extreme narrow case (stage D), via the width-bound cell term.
 *
 * ZERO NATIVE VIEWPORT UNITS (T-7.2.5-fix4): also sets `--vw` / `--vh` / `--vmin`
 * (px per 1 unit) and `--dvh` (the LIVE visible height in px — `visualViewport.height`,
 * tracks the mobile address bar; was `clientHeight` until the 2026-06-23 portrait fix)
 * on :root, measured from `clientWidth/clientHeight`. CSS uses these instead of native `vw`/`vmin`/`dvh`
 * (`calc(N * var(--vw, 1vw))` etc.), with the native unit only as a pre-JS
 * fallback. WHY: even AFTER the board geometry was JS-measured, the residual
 * native viewport units in `--fs-*` (vw), `--btn-h`/`--btn-h-compact` (vmin) and
 * the `100dvh`/`70dvh` heights still resolved slightly differently per engine
 * w.r.t. the scrollbar — so fonts + buttons (daily/result/teaser/leaderboard)
 * differed between Chrome and Firefox. JS-measured drop-ins make every length
 * byte-identical across engines.
 *
 * FULL FONT/BUTTON TOKEN SCALE IN JS (T-7.2.5-fix5): even after fix4's `--vw`/
 * `--vmin` drop-ins, the `:root --fs-*`/`--btn-h*` tokens still contained CSS
 * `calc(N * var(--vw, 1vw))` math. Real Firefox evaluated those font/button
 * `calc(...var(--vw)...)` expressions DIFFERENTLY from Chrome on non-board
 * surfaces (e.g. `.teaser-countdown` 65.2px Chrome vs 79.2px Firefox at a 1366px
 * client — Firefox behaved as if vw≈18; `getComputedStyle(:root).getPropertyValue
 * ('--vw')` even returns '' in Firefox for the inline custom prop). The BOARD
 * fonts proved the cure: they are `--cell-size`-based (a JS px value) and matched
 * perfectly across engines, while a CSS viewport-unit calc did not. So fix5
 * computes the WHOLE font/button token scale in JS, as plain px, using the SAME
 * formulas global.css declares (coefficients preserved exactly — the visual scale
 * the owner approved is unchanged), and sets them inline on :root. After JS runs
 * (before first paint via the inline <head> script), NO surface computes a font or
 * button size from a CSS viewport-unit calc — every value is a JS-measured px,
 * identical across engines. The `--fs-*`/`--btn-h*` calc-with-vw in global.css
 * survives ONLY as the unused pre-JS fallback (which must match this math).
 *
 * RACE FIX (T-7.2.5-fix3, owner-incident teaser/leaderboard Chrome≠Firefox):
 *   The same geometry is ALSO inlined as a blocking <head> script in index.html,
 *   so `--cell-size`/`--m`/`data-layout` are set BEFORE first paint in EVERY
 *   engine — no frame ever renders with the pre-JS CSS fallback (`--cell-size:
 *   40px`, no `data-layout`). Previously the first deterministic write happened
 *   only when this module executed (deferred module script). If
 *   `documentElement.clientWidth` was 0 at that instant in one engine, `apply()`
 *   early-returned and `data-layout` stayed unset until the ASYNC ResizeObserver
 *   fired AFTER first paint → Chrome painted the rail layout, Firefox painted the
 *   stacked fallback for the SAME code (the teaser split-vs-stacked divergence;
 *   the leaderboard scale divergence — its `max-width: var(--board-w)` resolved
 *   off the 40px fallback cell in one engine, the JS cell in the other). The
 *   constants here are the SINGLE SOURCE OF TRUTH; the inline snippet is a literal
 *   transcription and `tests/lib/viewport-sizer-inline.test.ts` pins the two so
 *   they can never drift.
 *
 * DIRTY-CHECK + rAF COALESCING (perf, brief-43, zero-pixel change): triggers
 * (ResizeObserver/resize/orientationchange/pageshow) fire on every tick even when
 * the floored geometry is UNCHANGED. `apply()` now (1) coalesces bursts into one
 * applyCore() per animation frame and (2) skips ALL :root writes + the
 * 'viewport-applied' dispatch when the serialized ViewportGeometry is byte-identical
 * to the last applied one — so no consumer wakes for a no-op resize. The FIRST write
 * (install) runs applyCore() synchronously (pre-paint parity preserved). Behaviour is
 * unchanged for any real geometry change.
 */

// Geometry constants (cell-units) — design-tokens-unified.md §3.2 + §14.10.2.
// EXPORTED so tests can pin the inline <head> snippet (index.html) to this math.
export const PORTRAIT_W_CELLS = 10.2; // board 9×9 + frame padding, horizontal
export const PORTRAIT_H_CELLS = 11.28; // board + niche row, vertical
export const BOARD_W_CELLS = 10.2; // raw board width in cells (= PORTRAIT_W_CELLS)
export const LANDSCAPE_H_CELLS = 11.28;
export const SIDE_RAIL_ASPECT = 0.8; // 4/5 — at/above this (w/h) we use the side-rail layout
export const M_FLOOR_PX = 8; // tactile floor for --m

/**
 * `rail_w_min` — tactile floor for the side-rail PANEL width (§14.10.2).
 *
 * The "постамент" centering needs a SYMMETRIC reserve so the centred board can
 * never run into the panel. The minimum a panel can shrink to is one tappable
 * icon-button column: `.btn--icon` is `min-width: var(--btn-h)`, whose floor is
 * 44px (iOS HIG / Apple touch target — the same P2 tactile minimum the board
 * action buttons use). So 44px is the smallest a still-tappable panel can be.
 * It is a floor CONSTANT (not a magic layout number): the width-bound cell term
 * only engages at the extreme narrow case (stage D), reserving exactly 2× this
 * floor (left + right margins) around the centred board.
 */
export const RAIL_W_MIN_PX = 44;

/** Cell-multiple for the preferred (full) panel width — game rail = 5·cell. */
export const RAIL_W_PREF_CELLS = 5;

// No explicit :root font-size → browser default 16px in every engine. The CSS
// `rem` unit in the global.css fallback formulas resolves to this; transcribing it
// here keeps the JS-computed px byte-identical to the (now unused) CSS fallback.
export const REM_PX = 16;

/**
 * Button label legibility floor (px) — design-tokens-unified.md §3.3: the `13` in
 * `--fs-button = max(13px, …)`. The SMALLEST a board action label is allowed to be
 * before it stops being legible. Named here (single source) so the compact-rail
 * trigger (compact-rail.md AC-4) can reuse the SAME documented design minimum as a
 * measured threshold — NOT a guessed layout constant. Used in computeTokenScale's
 * fsButton floor below AND exported for the compact decision.
 */
export const FS_BUTTON_MIN_PX = 13;

/**
 * Font/button token scale — design-tokens-unified.md §3.3/§3.4, transcribed
 * VERBATIM from the global.css `:root` fallback formulas (same coefficients, same
 * `max()` floors, rem=16). Computing them in JS (plain px) makes every font/button
 * length byte-identical across engines — a CSS `calc(N * var(--vw))` did not (fix5).
 *
 * Each token below maps 1:1 to its global.css definition:
 *   --fs-title      max(18px, 0.9rem  + 0.85vw)
 *   --fs-subtitle   max(14px, 0.8rem  + 0.35vw)
 *   --fs-body       max(13px, 0.7rem  + 0.30vw)
 *   --fs-label      max(10px, 0.55rem + 0.22vw)
 *   --fs-button     max(13px, 0.75rem + 0.35vw)
 *   --fs-kpi-k      max(11px, 0.6rem  + 0.22vw)
 *   --fs-kpi-v      max(20px, 1rem    + 0.70vw)
 *   --fs-countdown  max(28px, 1rem    + 2.50vw)
 *   --btn-h         max(44px, cell·0.7)         (board-based — owner 2026-06-05; was 2.6vmin)
 *   --btn-h-compact max(38px, cell·0.7·38/44)   (same 38:44 floor proportion as full)
 * where vw = w/100; --btn-h/* are now cell-based (vmin no longer used for them).
 */
export interface TokenScale {
  fsTitle: number;
  fsSubtitle: number;
  fsBody: number;
  fsLabel: number;
  fsButton: number;
  fsKpiK: number;
  fsKpiV: number;
  fsCountdown: number;
  btnH: number;
  btnHCompact: number;
}

/** Pure: compute the font/button token scale (px) from measured client dims. */
export function computeTokenScale(w: number, cell: number): TokenScale {
  const vw = w / 100;
  return {
    fsTitle: Math.max(18, 0.9 * REM_PX + 0.85 * vw),
    fsSubtitle: Math.max(14, 0.8 * REM_PX + 0.35 * vw),
    fsBody: Math.max(13, 0.7 * REM_PX + 0.3 * vw),
    fsLabel: Math.max(10, 0.55 * REM_PX + 0.22 * vw),
    fsButton: Math.max(FS_BUTTON_MIN_PX, 0.75 * REM_PX + 0.35 * vw),
    fsKpiK: Math.max(11, 0.6 * REM_PX + 0.22 * vw),
    fsKpiV: Math.max(20, 1 * REM_PX + 0.7 * vw),
    fsCountdown: Math.max(28, 1 * REM_PX + 2.5 * vw),
    // Tactile control height = BOARD (cell·0.7) — same standard as the «?»-trigger
    // (--ipt-size) + header band; floor 44px. Board-derived → grows with the board on
    // big screens (owner-directive 2026-06-05; was vmin-based, capped at 44 до ~1920).
    // --btn-h drives BOTH controls (icon-button/toggle/theme) AND plashka thickness
    // (panel-row = --btn-h + air) → plashka board-based, control ≤ plashka preserved.
    // Compact = cell·0.7·(38/44) — same floor proportion 38:44 as full.
    btnH: Math.max(44, cell * 0.7),
    btnHCompact: Math.max(38, (cell * 0.7 * 38) / 44),
  };
}

/**
 * @param w  layout clientWidth (width axis — unchanged, ADR-0010/SS-1570).
 * @param h  layout clientHeight — the height the board is sized from (height-bound
 *   cell term, landscape AND portrait chrome budget) AND the rail/stacked aspect
 *   decision (`w/h`). Sourcing the cell from the STATIC layout height (≡ lvh) is the
 *   reverted-to behaviour (ticket D / AC-D1): the 2b experiment that fed the board a
 *   LIVE `visualViewport.height` here was an owner error-перепутка (the daily board
 *   was fine; owner asked for the LEADERBOARD) and is reverted. NOTE: `--dvh` (the
 *   screen-root HEIGHT) still tracks the live visualViewport — that is a separate fix
 *   (white-band 2026-06-23, AC-D2) and is NOT reverted; only the board-CELL coupling
 *   to the live height is undone here.
 * @param m  the delta.
 */
export function computeCell(w: number, h: number, m: number): number {
  const aspect = w / h;
  if (aspect >= SIDE_RAIL_ASPECT) {
    // Side-rail (landscape + near-square): the board is TRUE-CENTRED (§14.10.1),
    // so the horizontal reserve must be SYMMETRIC — a min panel + delta-gap on
    // EACH side, not just one rail on the right (old 15.6 = board+rail+gap, which
    // allowed the board to drift left into the panel — the 0c1bbca bug).
    //
    //   right margin (min) = delta(screen) + rail_w_min + delta(gap) = 2m + rail_w_min
    //   true-centre ⇒ left margin = right margin
    //   no-overlap: board_w ≤ vw − 2·(2m + rail_w_min) = vw − 4m − 2·rail_w_min
    //   ⇒ cell_width-bound = (vw − 4m − 2·rail_w_min) / 10.2
    //
    // The board stays HEIGHT-bound (full) until this very narrow case (stage D).
    // Height-bound term uses the STATIC layout `h` (ticket D revert — was the LIVE
    // visH under 2b; the daily board needed no live-height coupling).
    return Math.min(
      (h - 2 * m) / LANDSCAPE_H_CELLS,
      (w - 4 * m - 2 * RAIL_W_MIN_PX) / BOARD_W_CELLS,
    );
  }
  // Stacked portrait: board on top, vertical chrome above (one-line header) +
  // below (KPI strip + actions row). The OLD height term `(h-2m)/11.28` reserved
  // ONLY the board's own height — so on a wide-ish portrait (768×1024, 820×1180)
  // the board went WIDTH-bound, filled the width, and left NO vertical room for
  // chrome → the board overflowed its grid track and overlapped the header / KPI /
  // buttons (owner-incident 2026-05-29 «наезды в вертикальном режиме»). Fix: the
  // height-bound term now RESERVES the portrait chrome budget, so the board shrinks
  // (stage-D board-shrink-for-chrome §14.10.2 — sanctioned, board-as-etalon keeps
  // board==preview because the SAME formula sizes both). Shared → fixes board AND
  // daily at once (owner-directive «одни и те же подходы для всех окон»).
  //
  // Chrome budget (px), every term traces to a real postament CSS token formula
  // (no magic; px-floors = legibility/tactile minimums, CLAUDE.md-allowed):
  //   • 3 inter-row gaps  : 3 · --gap-chrome  = 3 · max(4px, cell·0.45)
  //   • actions row       : .postament-actions .btn min-height = max(44px, cell·1.3)
  //   • one-line header   : шапка-полоса (owner-directive 2026-05-31, вариант 2) —
  //                         high = max(44px, cell·0.7): floor 44 = тактическая высота
  //                         ☰/«?» (бургер app-shell + info-popover trigger помещаются
  //                         в полосу, контент ниже уходит из-под ☰). Совпадает с
  //                         postament `.postament-header min-height` + info-popover
  //                         trigger size (одна формула в трёх местах).
  //   • KPI secondary     : фикс-высота max(66px, cell·1.85) = подпись (fs-kpi-k
  //                         line ≈15px) + кнопочные цифры таймера (max(44px, cell·1.3));
  //                         board «Свободно/Ходы» занимает её же → no-jump (Δtop=0)
  //                         (31 = 11px label floor + 20px value floor)
  // cell appears on both sides (chrome scales with cell) → iterate to a fixed
  // point (4 passes converge; starts from the width-bound candidate, monotone down).
  const cellW = (w - 2 * m) / PORTRAIT_W_CELLS;
  let cell = cellW;
  for (let i = 0; i < 4; i++) {
    const chrome =
      3 * Math.max(4, cell * 0.45) +
      Math.max(44, cell * 1.3) +
      Math.max(44, cell * 0.7) + // header band: floor 44 (☰/«?» tactile) — owner 2026-05-31
      Math.max(66, cell * 1.85);
    // Height-bound term from the STATIC layout `h` (ticket D revert — was the LIVE
    // visH under 2b). The board sizes from the layout height, same as before 2b.
    cell = Math.min(cellW, (h - 2 * m - chrome) / PORTRAIT_H_CELLS);
  }
  return cell;
}

/**
 * Side-rail panel width (`--rail-w`) — the flex token that lets the panel shrink
 * while the board stays centred and the board↔panel gap stays = delta (§14.10.2).
 *
 *   rail_w = clamp( rail_w_min , (vw − board_w)/2 − 2m , rail_w_pref )
 *
 * Algebra check (true-centre ⇒ left margin == right margin == (vw − board_w)/2):
 *   gap = right_margin − delta(screen) − rail_w = (vw−board_w)/2 − m − rail_w.
 *   With rail_w ≤ (vw−board_w)/2 − 2m ⇒ gap ≥ m always (owner: "не меньше дельты").
 *   Wide viewport → the middle term ≥ rail_w_pref ⇒ rail_w = rail_w_pref (full).
 *   Narrowing → the middle term falls to rail_w_min ⇒ panel shrinks, never below.
 *
 * @param cell floored integer cell px (the board-as-etalon source of truth).
 * @param w measured clientWidth, @param m the delta.
 */
export function computeRailW(cell: number, w: number, m: number): number {
  const boardW = boardShortSideForCell(cell);
  const railPref = RAIL_W_PREF_CELLS * cell;
  const flex = (w - boardW) / 2 - 2 * m;
  // clamp(rail_w_min, flex, rail_w_pref)
  return Math.max(RAIL_W_MIN_PX, Math.min(flex, railPref));
}

/**
 * Board short side (px) for a given floored cell — the SINGLE formula for the board's
 * outer square: 9 cells + 8 inter-cell gaps (`gap-b` = cell·0.08, floor 1) + 2 frame
 * paddings (`fpad` = cell·0.28, floor 4). Mirrors board.css/global.css derivation; used
 * by `computeRailW` (the side-rail panel width algebra).
 *
 * NOTE: the §5.9 replay board-floor (REPLAY_BOARD_FLOOR_PX / cellForBoardShortSide /
 * replayBoardFloorCell + lib/replay-board-floor.ts) was DELETED 2026-06-24 — owner отменил
 * §5.9 «floor exception» для реплея (директива №1, `feedback_owner_directives_narrow_v3.md`):
 * доска реплея ВСЕГДА вписана во вьюпорт как живая (board-as-etalon), НИКОГДА не больше
 * экрана; крупнее на узком делаем ТОЛЬКО транспорт-кнопки. `boardShortSideForCell` остаётся,
 * потому что его использует `computeRailW` (общая формула доски, не floor-специфика).
 */
export function boardShortSideForCell(cell: number): number {
  const gapB = Math.max(1, Math.round(cell * 0.08));
  const fpad = Math.max(4, Math.round(cell * 0.28));
  return 9 * cell + 8 * gapB + 2 * fpad;
}

/** Computed geometry for a measured container — the single deterministic write. */
export interface ViewportGeometry {
  cell: number;
  m: number;
  layout: 'rail' | 'stacked';
  /** Side-rail panel width (px) — flex between rail_w_min and 5·cell (§14.10.2). */
  railW: number;
  /** JS-measured drop-ins for native viewport units (no `100vw`-vs-scrollbar). */
  vw: number; // px per `1vw`   = clientWidth / 100
  vh: number; // px per `1vh`   = clientHeight / 100
  vmin: number; // px per `1vmin` = min(clientWidth, clientHeight) / 100
  // px for `100dvh` = the LIVE VISIBLE height (window.visualViewport.height when
  // available, else clientHeight). NOT the static layout clientHeight — that was the
  // white-band bug (ticket fe-mobile-viewport-height-2026-06-23): clientHeight ≡ lvh
  // (address bar HIDDEN) and does NOT shrink when the bar is SHOWN, so the screen-root
  // stayed lvh-tall and left a band under the bottom row while the bar was visible.
  // Sourcing it from the visual viewport makes --dvh track the bar show/hide in real
  // time (fed by a visualViewport listener in installViewportSizer). SKILL §0.A.1.
  dvh: number;
  /** Font/button token scale (px) — computed in JS so it is engine-identical (fix5). */
  tokens: TokenScale;
}

/**
 * Тип указателя устройства: true = «грубый» (тач), false = «точный» (мышь). Читаем из
 * CSS-media `(pointer: coarse)` через matchMedia — тот же канон device-type, что в CSS
 * (responsive-ui §5.1), но в JS, чтобы записать его как СТАБИЛЬНЫЙ data-атрибут (CSS
 * `@media (orientation)` в Playwright fullPage-скриншоте резолвится иначе → стопка не
 * попадала в baseline). SSR/без matchMedia → fine (десктоп-дефолт, безопасно).
 */
function detectPointerCoarse(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(pointer: coarse)').matches;
}

/**
 * Pure: derive geometry from measured client dims. Returns null if not laid out yet.
 *
 * @param w  layout clientWidth (geometry/width axis — unchanged, ADR-0010/SS-1570).
 * @param h  layout clientHeight (used for the cell aspect + portrait chrome budget).
 * @param visH  the LIVE VISIBLE height for `--dvh` (window.visualViewport.height when
 *   available, else the layout `h`). Defaults to `h` so callers that only know the
 *   layout height (tests, the pre-JS fallback) still get a sane value; the live caller
 *   (applyCore) passes the visualViewport height so `--dvh` tracks the address bar.
 */
export function computeGeometry(w: number, h: number, visH: number = h): ViewportGeometry | null {
  if (w <= 0 || h <= 0) return null;
  // --m delta: tactile floor 8px, else 1% of the short side (no viewport units).
  const m = Math.max(M_FLOOR_PX, 0.01 * Math.min(w, h));
  // Floor cell to an integer pixel → crisp grid + fully deterministic derived chain.
  // computeCell sizes the board from the STATIC layout `h` (ticket D revert — 2b's
  // live-visH coupling to the CELL is undone; only `--dvh` below stays live). AC-D1.
  const cell = Math.max(1, Math.floor(computeCell(w, h, m)));
  // Same SIDE_RAIL_ASPECT (4/5) threshold over the MEASURED container so the
  // decision can never flip on the 100vw-vs-scrollbar boundary.
  const layout: 'rail' | 'stacked' = w / h >= SIDE_RAIL_ASPECT ? 'rail' : 'stacked';
  // JS-measured viewport-unit equivalents (§14.9 fix4): `--vw`/`--vmin`/`--vh` are
  // px-per-1-unit, `--dvh` is the full height in px. These replace native
  // `vw`/`vmin`/`dvh` in CSS, which Chrome and Firefox resolve differently w.r.t.
  // the classic scrollbar — measuring `clientWidth/Height` is identical per engine.
  const vw = w / 100;
  const vh = h / 100;
  const vmin = Math.min(w, h) / 100;
  // --dvh = the LIVE VISIBLE height (visualViewport.height, passed in as visH), NOT
  // the layout clientHeight (≡ lvh, the white-band bug). Defaults to the layout height
  // for callers that don't measure the visual viewport. SKILL §0.A.1; ticket AC-5.
  const dvh = visH;
  // Font/button token scale (fix5): the SAME formulas global.css declares, in JS
  // px — no CSS viewport-unit calc for fonts/buttons → engine-identical sizes.
  const tokens = computeTokenScale(w, cell);
  // Side-rail panel flex width (§14.10.2). Computed in JS (not a grid spacer) so
  // the panel shrinks deterministically while the board stays true-centred.
  const railW = computeRailW(cell, w, m);
  return { cell, m, layout, railW, vw, vh, vmin, dvh, tokens };
}

/**
 * Last geometry actually written to :root, serialized. Lets applyCore() skip ALL
 * :root writes + the 'viewport-applied' dispatch when a resize tick produced a
 * byte-identical geometry (the common no-op case — e.g. mobile address-bar jitter,
 * scrollbar appearance, sub-floor width changes that don't move floor(cell)).
 * Zero-pixel change: the SAME geometry is applied; we just don't re-apply it.
 */
let lastAppliedGeometryJson: string | null = null;

/** Write the computed geometry onto :root. No-op when not laid out yet OR unchanged. */
function applyCore(): void {
  const el = document.documentElement;
  // Layout height read ONCE into a local `ch` (so the literal `clientHeight` access is
  // NOT inside the `--dvh` source expression — the AC-5 guard traces the write back to
  // its source; the source must be the VISIBLE viewport, not the layout clientHeight).
  const ch = el.clientHeight;
  // LIVE VISIBLE height (source of `--dvh`): window.visualViewport.height — it shrinks
  // with the mobile address bar / on-screen keyboard, unlike the layout clientHeight
  // (≡ lvh, which stays put when the bar shows → the white-band bug; ticket
  // fe-mobile-viewport-height-2026-06-23, AC-5). During a pinch-zoom vv.height is the
  // ZOOMED height — using it would shrink/stretch the whole layout with the gesture
  // (R4/AC-6), so fall back to the layout height `ch` while scale ≠ 1 (or no vv).
  const vv = typeof window !== 'undefined' ? window.visualViewport : null;
  const liveDvh = vv && vv.scale === 1 && vv.height > 0 ? vv.height : ch;
  // Width/aspect from the layout clientWidth/clientHeight (unchanged width axis,
  // ADR-0010); --dvh from the LIVE VISIBLE height (liveDvh) so the screen-root tracks
  // the mobile address bar / keyboard in real time.
  const g = computeGeometry(el.clientWidth, ch, liveDvh);
  if (!g) return;
  // Dirty-check: ViewportGeometry is EXACTLY what we write to :root (cell/m/layout/
  // railW/vw/vh/vmin/dvh + token scale). If it is identical to the last applied one,
  // every setProperty below would be a no-op and the dispatch would wake 5 consumers
  // for nothing → skip entirely (brief-43). Serialize once, compare, apply+store only
  // on a real change (never store on the early-return paths above).
  const json = JSON.stringify(g);
  if (json === lastAppliedGeometryJson) return;
  el.style.setProperty('--cell-size', `${g.cell}px`);
  el.style.setProperty('--m', `${Math.round(g.m)}px`);
  // Side-rail panel flex width (§14.10.2) — overrides the global.css fallback
  // (calc(--cell-size * 5)). Lets the panel shrink to rail_w_min while the board
  // stays true-centred via justify-content (NOT a grid spacer — the 0c1bbca bug).
  el.style.setProperty('--rail-w', `${g.railW}px`);
  // JS-measured drop-ins for native viewport units (kept full-precision; CSS
  // multiplies them by the per-rule coefficient). Identical across engines.
  el.style.setProperty('--vw', `${g.vw}px`);
  el.style.setProperty('--vh', `${g.vh}px`);
  el.style.setProperty('--vmin', `${g.vmin}px`);
  // --dvh from the LIVE VISIBLE height (liveDvh ← window.visualViewport.height), NOT
  // the layout clientHeight. liveDvh === g.dvh numerically (same source passed into
  // computeGeometry); written from liveDvh so the height source is unambiguously the
  // visible viewport (AC-5: forbids a clientHeight one-shot). SKILL §0.A.1.
  el.style.setProperty('--dvh', `${liveDvh}px`);
  // Font/button tokens in JS px (fix5) — overrides the global.css calc-with-vw
  // fallback so no surface evaluates a font/button size from a CSS viewport unit
  // (Firefox resolved those calcs differently from Chrome → divergent text).
  const t = g.tokens;
  el.style.setProperty('--fs-title', `${t.fsTitle}px`);
  el.style.setProperty('--fs-subtitle', `${t.fsSubtitle}px`);
  el.style.setProperty('--fs-body', `${t.fsBody}px`);
  el.style.setProperty('--fs-label', `${t.fsLabel}px`);
  el.style.setProperty('--fs-button', `${t.fsButton}px`);
  el.style.setProperty('--fs-kpi-k', `${t.fsKpiK}px`);
  el.style.setProperty('--fs-kpi-v', `${t.fsKpiV}px`);
  el.style.setProperty('--fs-countdown', `${t.fsCountdown}px`);
  el.style.setProperty('--btn-h', `${t.btnH}px`);
  el.style.setProperty('--btn-h-compact', `${t.btnHCompact}px`);
  el.dataset.layout = g.layout;
  // data-pointer = тип указателя устройства (coarse=тач / fine=мышь), JS-driven из
  // matchMedia. SET-ONCE: пишем ТОЛЬКО если ещё не выставлен. Источник истины —
  // inline-<head>-bootstrap (index.html), который ставит его ДО первого пэйнта тем
  // же matchMedia('(pointer: coarse)') (FOUC-фикс стопки режимов, тикет fe-home-screen
  // правка 1). Тип указателя — статичная характеристика устройства за сессию; раньше
  // applyCore РЕ-вычислял matchMedia на КАЖДОМ ресайзе/RO-тике и затирал значение.
  // Это ломало baseline: Playwright toHaveScreenshot({fullPage:true}) ресайзит вьюпорт
  // для съёмки → ResizeObserver → applyCore → matchMedia('(pointer: coarse)') в этот
  // момент резолвился как FALSE (mobile-эмуляция слетала на fullPage-ресайзе) →
  // data-pointer щёлкал coarse→fine → стопка режимов разваливалась в ряд ПРЯМО во
  // время скриншота. Set-once = значение первого пэйнта стабильно (на реальном
  // устройстве matchMedia всё равно не меняется → поведение идентично). Парный с
  // data-layout: `:root[data-pointer='coarse'][data-layout='stacked']` = ТОЛЬКО
  // телефон-портрет (PC-портрет = fine → НЕ стопка, AC-18). device-type, НЕ width.
  if (el.dataset.pointer !== 'coarse' && el.dataset.pointer !== 'fine') {
    el.dataset.pointer = detectPointerCoarse() ? 'coarse' : 'fine';
  }
  // Сигнал «геометрия применена» — для потребителей, которым нужно пересчитаться
  // ПОСЛЕ установки токенов (напр. autofit-text меряет ширину блоков). Иначе
  // авто-подбор, привязанный к размеру screen-root, пропускает изменения, которые
  // меняют ТОЛЬКО токены (--cell-size/--rail-w), а не размер вьюпорта — напр.
  // расхождение inline-head (innerWidth) vs module (clientWidth) на первой загрузке
  // → текст оставался подобранным под устаревшую геометрию до ручного ресайза.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('lines98:viewport-applied'));
  }
  // Record the applied state ONLY after a real apply (not on early-returns) so the
  // next tick can skip an identical geometry.
  lastAppliedGeometryJson = json;
}

/**
 * Coalesce a burst of resize/RO/orientation ticks into ONE applyCore() per animation
 * frame (perf, brief-43). ResizeObserver can fire many times during a drag-resize or
 * address-bar animation; without coalescing each tick recomputes + (pre-dirty-check)
 * rewrote :root. We schedule a single rAF; subsequent calls in the same frame are
 * dropped. The dirty-check inside applyCore() then no-ops when nothing changed.
 */
let rafScheduled = 0;
function apply(): void {
  // SSR / no-rAF env: apply synchronously (mirrors the dispatch SSR-guard pattern).
  if (typeof window === 'undefined' || typeof requestAnimationFrame === 'undefined') {
    applyCore();
    return;
  }
  if (rafScheduled) return;
  rafScheduled = requestAnimationFrame(() => {
    rafScheduled = 0;
    applyCore();
  });
}

/** Имя события, диспатчится из applyCore() после установки всех токенов на :root. */
export const VIEWPORT_APPLIED_EVENT = 'lines98:viewport-applied';

let installed = false;

export function installViewportSizer(): void {
  if (installed) return;
  installed = true;
  // Re-apply once, SYNCHRONOUSLY (not via the rAF-coalescing apply()). The inline
  // <head> snippet already did the FIRST write before paint (the race fix); this
  // picks up any head-time-vs-module-time change and is a harmless no-op when
  // identical. Running applyCore() directly keeps the first frame byte-identical to
  // the pre-coalescing behaviour (no deferral to the next frame; brief-43).
  applyCore();
  // Live updates: ResizeObserver on the root element catches viewport resizes
  // (incl. mobile address-bar show/hide) more reliably than the resize event.
  if (typeof ResizeObserver !== 'undefined') {
    const ro = new ResizeObserver(() => apply());
    ro.observe(document.documentElement);
  }
  window.addEventListener('resize', apply, { passive: true });
  window.addEventListener('orientationchange', apply, { passive: true });
  // LIVE address-bar / keyboard tracking (ticket fe-mobile-viewport-height-2026-06-23).
  // The mobile browser bar show/hide is a `window.visualViewport` event — the layout
  // viewport (clientHeight ≡ lvh) does NOT change, so ResizeObserver/resize alone
  // MISS it (that was the white-band bug: --dvh stayed locked to lvh while the bar was
  // shown). We listen to visualViewport `resize` (bar collapse/expand, keyboard) and
  // `scroll` (offsetTop shifts as the bar animates on scroll) and route through the
  // SAME rAF-coalescing apply() → applyCore() reads the live visualViewport.height and
  // the dirty-check no-ops when nothing actually changed (no new perf path; AC-6).
  // Pinch-zoom is filtered inside applyCore (scale ≠ 1 → fall back to layout height),
  // so a zoom gesture never collapses/stretches the layout (AC-6 / R4).
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', apply, { passive: true });
    window.visualViewport.addEventListener('scroll', apply, { passive: true });
  }
  // bfcache restore (браузерная кнопка «Назад» → возврат): страница поднимается из
  // back-forward cache без повторного исполнения <head>-сниппета; токены могли
  // «протухнуть» относительно живого layout → пере-применяем геометрию на pageshow
  // (persisted). Освежает --cell-size/--m/... для ВСЕХ экранов (incl. lobby).
  window.addEventListener('pageshow', (e: PageTransitionEvent) => {
    if (e.persisted) apply();
  });
}
